import {
  Dispatch,
  PropsWithChildren,
  createContext,
  use,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Config, SupportedLocales, initialConfig } from 'config';
import { getItemFromStore } from 'lib/utils';
import { getAccessToken } from 'utils/sessionStore';
import { languages } from 'locales/languages';
import {
  ACTIONTYPE,
  COLLAPSE_NAVBAR,
  EXPAND_NAVBAR,
  SET_CONFIG,
  settingsReducer,
} from 'reducers/SettingsReducer';

const detectInitialLocale = (): SupportedLocales => {
  if (typeof navigator === 'undefined') return initialConfig.locale;
  const candidates = [navigator.language, ...(navigator.languages ?? [])];
  for (const candidate of candidates) {
    const tag = candidate.toLowerCase();
    const match = languages.find((lang) => {
      const localeTag = lang.locale.toLowerCase();
      return tag === localeTag || tag.split('-')[0] === localeTag.split('-')[0];
    });
    if (match) return match.locale;
  }
  return initialConfig.locale;
};

interface SettingsContextInterFace {
  config: Config;
  configDispatch: Dispatch<ACTIONTYPE>;
  setConfig: (payload: Partial<Config>) => void;
  handleDrawerToggle: () => void;
  toggleNavbarCollapse: () => void;
}

export const SettingsContext = createContext({} as SettingsContextInterFace);

const SettingsProvider = ({ children }: PropsWithChildren) => {
  const configState: Config = {
    ...initialConfig,
    sidenavCollapsed: getItemFromStore('sidenavCollapsed', initialConfig.sidenavCollapsed),
    sidenavType: getItemFromStore('sidenavType', initialConfig.sidenavType),
    topnavType: getItemFromStore('topnavType', initialConfig.topnavType),
    textDirection: getItemFromStore('textDirection', initialConfig.textDirection),
    navigationMenuType: getItemFromStore('navigationMenuType', initialConfig.navigationMenuType),
    navColor: getItemFromStore('navColor', initialConfig.navColor),
    locale: getItemFromStore('locale', detectInitialLocale()),
  };
  const [config, configDispatch] = useReducer(settingsReducer, configState);
  const { i18n } = useTranslation();

  const setConfig = (payload: Partial<Config>) => {
    configDispatch({
      type: SET_CONFIG,
      payload,
    });
  };

  const handleDrawerToggle = () => {
    setConfig({
      openNavbarDrawer: !config.openNavbarDrawer,
    });
  };

  const toggleNavbarCollapse = () => {
    if (config.sidenavCollapsed) {
      configDispatch({
        type: EXPAND_NAVBAR,
      });
    } else {
      configDispatch({
        type: COLLAPSE_NAVBAR,
      });
    }
  };

  useEffect(() => {
    i18n.changeLanguage(config.locale.split('-').join(''));
  }, [config.locale]);

  // Persist the locale to the BE so transactional emails (verify, welcome,
  // password reset, billing, retention, etc.) honor the language the user
  // is browsing in. We only fire after the initial mount — that first run
  // would otherwise hit the API on every page load with no actual change.
  // Anonymous users (no auth token) are silently skipped; their language is
  // captured at signup via the `preferredLanguage` field on /create-free-event.
  const localeSyncMounted = useRef(false);
  useEffect(() => {
    if (!localeSyncMounted.current) {
      localeSyncMounted.current = true;
      return;
    }
    if (typeof window === 'undefined') return;
    if (!getAccessToken()) return; // sandbox-aware: demo token counts as a session

    const beLang = config.locale.toLowerCase().startsWith('el') ? 'el' : 'en';

    (async () => {
      try {
        const { updateCoupleLanguage } = await import('services/api/couple');
        await updateCoupleLanguage(beLang);
      } catch (err) {
        // Non-blocking — the FE has already switched locales. Surface to
        // console only; users without a couple session (e.g. attendees) will
        // get a 401 here and that's expected.
        console.warn('[settings] Failed to persist language preference:', err);
      }
    })();
  }, [config.locale]);

  return (
    <SettingsContext
      value={{
        config,
        configDispatch,
        setConfig,
        handleDrawerToggle,
        toggleNavbarCollapse,
      }}
    >
      {children}
    </SettingsContext>
  );
};

export const useSettingsContext = () => use(SettingsContext);

export default SettingsProvider;
